// Operator-console session guard (ADR-007 / spec 60).
//
// The operators realm is a separate identity boundary from customer users.
// This guard only controls the protected SPA route tree; every /ops/v1/*
// endpoint remains independently authenticated and authorized server-side.

import { useEffect, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router";

import { getSigninState } from "./return-to";

export interface RequireOperatorAuthProps {
  readonly children: ReactNode;
}

/**
 * Starts the operators-realm authorization-code redirect once OIDC has
 * resolved an unauthenticated protected route. `/callback` is deliberately
 * outside this component in router.tsx so the redirect response can complete.
 */
export function RequireOperatorAuth({ children }: RequireOperatorAuthProps): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      auth.activeNavigator === undefined &&
      !auth.error
    ) {
      void auth.signinRedirect({ state: getSigninState(location) });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, auth, location]);

  // A background renew error must not remove authenticated queries, even
  // after the old token expires: their next 401 drives explicit recovery.
  const recoverableBackgroundError = auth.error?.source === "renewSilent" && auth.isAuthenticated;
  if (auth.error && !recoverableBackgroundError) {
    return (
      <div className="p-6 text-sm text-[var(--color-foreground)]" role="alert">
        {t("auth.callback_error")}
      </div>
    );
  }

  if (auth.isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className="p-6 text-sm text-[var(--color-muted-foreground)]" aria-live="polite">
      {t("auth.loading")}
    </div>
  );
}
